import {Button} from '@common-web/components/ui/button';
import {cn} from '@common-web/lib/utils';
import {
  formatDate,
  type GroupPipelineDetail,
} from '@internal/pages/group-pipeline/components/pipelineDisplay';
import {StatusIndicator} from '@internal/pages/group-pipeline/components/StatusIndicator';
import {
  nextMoveActions,
  type PipelineTask,
  type TaskAction,
} from '@internal/pages/group-pipeline/detail/taskActions';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';

export function NextMoveCard({
  pipeline,
  canManage,
  isPending,
  onAction,
}: {
  pipeline: GroupPipelineDetail;
  canManage: boolean;
  isPending: boolean;
  onAction: (task: PipelineTask, action: TaskAction) => void;
}) {
  const current = pipeline.state.currentTask;
  const task = current ? pipeline.tasks.find((t) => t.id === current.id) : undefined;
  if (!task) return null;

  const isOverdue = pipeline.state.overdueTaskIds.includes(task.id);

  return (
    <div className='flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-white px-5 py-4'>
      <div className='space-y-1'>
        <p className='text-xs font-bold uppercase tracking-[0.07em] text-gray-500'>Next move</p>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span className='text-lg font-semibold'>{task.label}</span>
          {task.dueAt && (
            <span className='text-sm text-gray-500'>due {formatDate(task.dueAt)}</span>
          )}
          {task.attemptCount > 1 && (
            <span className='text-sm text-gray-500'>Attempt {task.attemptCount}</span>
          )}
          {isOverdue && (
            <StatusIndicator
              status='OVERDUE'
              context={{stepLabel: task.label, dueAt: task.dueAt, offReason: null}}
            />
          )}
        </div>
      </div>

      {canManage && (
        <div className='flex flex-wrap items-center gap-2'>
          {nextMoveActions(task).map((action) => (
            <Button
              key={action.key}
              variant={action.primary ? 'default' : 'outline'}
              className={cn('relative', !action.primary && 'bg-white')}
              title={action.placeholder ? NOT_BUILT : undefined}
              disabled={isPending}
              onClick={() => onAction(task, action)}
            >
              {action.label}
              {action.placeholder && <PreviewDot />}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
